import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { itineraryApi } from '../services/api';
import { Calculator, MapPin, Car, Hotel, Camera, Loader2, Plus, X, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { BudgetResult, BudgetPlaceholder, OptionGrid } from '../components/budget/BudgetComponents';

const TRANSPORT_OPTIONS = [
  { value: 'budget', label: 'Budget (Public Transit, Walking)' },
  { value: 'economy', label: 'Economy (Mix of Public & Taxi)' },
  { value: 'comfort', label: 'Comfort (Taxi, Rideshare, Car Rental)' },
  { value: 'luxury', label: 'Luxury (Private Transfer, First Class)' },
];

const ACCOMMODATION_OPTIONS = [
  { value: 'budget', label: 'Budget (Hostels, Budget Hotels)' },
  { value: 'mid-range', label: 'Mid-Range (3-Star Hotels)' },
  { value: 'comfort', label: 'Comfort (4-Star Hotels)' },
  { value: 'luxury', label: 'Luxury (5-Star Hotels, Resorts)' },
];

const ACTIVITY_OPTIONS = [
  { value: 'minimal', label: 'Minimal (Free attractions, Walking tours)' },
  { value: 'moderate', label: 'Moderate (Mix of free & paid attractions)' },
  { value: 'active', label: 'Active (Tours, Experiences, Shows)' },
  { value: 'premium', label: 'Premium (VIP tours, Fine dining, Spa)' },
];

const CalculateBudgetPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [destinations, setDestinations] = useState([{ city: '', country: '', days: 3 }]);
  const [originCity, setOriginCity] = useState('');
  const [originCountry, setOriginCountry] = useState('');
  const [transportMode, setTransportMode] = useState('economy');
  const [accommodationType, setAccommodationType] = useState('mid-range');
  const [activities, setActivities] = useState('moderate');
  const [travelers, setTravelers] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [currencyMode, setCurrencyMode] = useState('original');
  const [convertedResult, setConvertedResult] = useState(null);
  const [convertingCurrency, setConvertingCurrency] = useState(false);

  const addDestination = () => setDestinations([...destinations, { city: '', country: '', days: 3 }]);
  const removeDestination = (idx) => {
    if (destinations.length <= 1) return;
    setDestinations(destinations.filter((_, i) => i !== idx));
  };
  const updateDestination = (idx, field, value) => {
    setDestinations(destinations.map((d, i) => i === idx ? { ...d, [field]: value } : d));
  };

  const handleCalculate = async () => {
    const validDests = destinations.filter(d => d.city.trim());
    if (validDests.length === 0) {
      toast.error('Please enter at least one destination');
      return;
    }
    setLoading(true);
    setResult(null);
    setConvertedResult(null);
    setCurrencyMode('original');
    try {
      const totalDays = validDests.reduce((sum, d) => sum + (parseInt(d.days) || 3), 0);
      const apiModule = await import('../services/api');
      const api = apiModule.default;
      const budgetRes = await api.post('/calculate-budget', {
        destinations: validDests,
        transport_mode: transportMode,
        accommodation_type: accommodationType,
        activity_level: activities,
        travelers: parseInt(travelers) || 1,
        total_days: totalDays,
        origin_city: originCity || null,
        origin_country: originCountry || null,
      });
      if (budgetRes.data.success) {
        setResult(budgetRes.data.budget);
      } else {
        toast.error(budgetRes.data.error || 'Failed to calculate budget');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to calculate budget');
    } finally {
      setLoading(false);
    }
  };

  const toggleCurrency = async () => {
    if (!result) return;
    if (currencyMode === 'converted' && convertedResult) {
      setCurrencyMode('original');
      return;
    }
    const nationalityCurrencyMap = {
      'australian': 'AUD', 'american': 'USD', 'british': 'GBP', 'canadian': 'CAD',
      'indian': 'INR', 'japanese': 'JPY', 'chinese': 'CNY', 'korean': 'KRW',
      'singaporean': 'SGD', 'malaysian': 'MYR', 'thai': 'THB', 'european': 'EUR',
      'german': 'EUR', 'french': 'EUR', 'new zealander': 'NZD',
    };
    const nationality = (user?.nationality || '').toLowerCase();
    const homeCurrency = nationalityCurrencyMap[nationality] || 'USD';
    const origCurrency = result.currency || 'USD';
    if (homeCurrency === origCurrency) {
      toast.info('Budget is already in your home currency');
      return;
    }
    setConvertingCurrency(true);
    try {
      const res = await itineraryApi.convertCurrency(1, origCurrency, homeCurrency);
      if (res.data.success && res.data.rate) {
        const rate = res.data.rate;
        const convert = (str) => {
          if (!str) return str;
          const match = String(str).match(/[\d,]+(?:\.\d+)?/);
          if (!match) return str;
          const num = parseFloat(match[0].replace(/,/g, ''));
          if (isNaN(num)) return str;
          return `${homeCurrency} ${Math.round(num * rate).toLocaleString()}`;
        };
        setConvertedResult({
          ...result,
          total: convert(result.total),
          per_person: convert(result.per_person),
          accommodation: convert(result.accommodation),
          transport: convert(result.transport),
          food: convert(result.food),
          activities: convert(result.activities),
          miscellaneous: convert(result.miscellaneous),
          currency: homeCurrency,
          per_destination: result.per_destination?.map(d => ({ ...d, total: convert(d.total) })),
        });
        setCurrencyMode('converted');
      }
    } catch {
      toast.error('Currency conversion failed');
    } finally {
      setConvertingCurrency(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="calculate-budget-page">
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-ocean via-ocean-light/90 to-sky/70" />
        <div className="absolute top-32 right-20 w-96 h-96 bg-gold/15 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-40 left-10 w-80 h-80 bg-sky/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}} />
      </div>

      <Header />

      <main className="pt-24 pb-16 px-6 md:px-12 lg:px-24 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white text-sm flex items-center gap-1 transition-colors" data-testid="back-to-dashboard-top">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <div className="w-16 h-16 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Calculator className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-white drop-shadow-lg mb-2">Calculate Budget</h1>
            <p className="text-white/80">Plan your trip budget before you travel</p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Form */}
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-ocean flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gold" /> Trip Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                  {/* Origin */}
                  <div>
                    <Label className="text-ocean font-medium mb-2 block">Traveling From</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input placeholder="City (e.g., Sydney)" value={originCity} onChange={(e) => setOriginCity(e.target.value)}
                        className="h-10 border-ocean/20" data-testid="origin-city-budget" />
                      <Input placeholder="Country (e.g., Australia)" value={originCountry} onChange={(e) => setOriginCountry(e.target.value)}
                        className="h-10 border-ocean/20" data-testid="origin-country-budget" />
                    </div>
                    <p className="text-xs text-ocean/50 mt-1">For accurate flight cost estimation</p>
                  </div>

                  {/* Destinations */}
                  <div>
                    <Label className="text-ocean font-medium mb-2 block">Destinations</Label>
                    <div className="flex gap-2 mb-1 text-xs text-ocean/50 px-1">
                      <span className="flex-1">City</span>
                      <span className="flex-1">Country</span>
                      <span className="w-20 text-center">Nights</span>
                      {destinations.length > 1 && <span className="w-10"></span>}
                    </div>
                    {destinations.map((dest, idx) => (
                      <div key={`dest-input-${idx}`} className="flex gap-2 mb-2 items-end">
                        <div className="flex-1">
                          <Input placeholder="City" value={dest.city} onChange={(e) => updateDestination(idx, 'city', e.target.value)}
                            className="h-10 border-ocean/20" data-testid={`dest-city-${idx}`} />
                        </div>
                        <div className="flex-1">
                          <Input placeholder="Country" value={dest.country} onChange={(e) => updateDestination(idx, 'country', e.target.value)}
                            className="h-10 border-ocean/20" data-testid={`dest-country-${idx}`} />
                        </div>
                        <div className="w-20">
                          <Input type="number" min={1} value={dest.days} onChange={(e) => updateDestination(idx, 'days', e.target.value)}
                            className="h-10 border-ocean/20 text-center" data-testid={`dest-days-${idx}`} />
                        </div>
                        {destinations.length > 1 && (
                          <Button variant="ghost" size="icon" className="h-10 w-10 text-red-400 hover:text-red-600"
                            onClick={() => removeDestination(idx)} data-testid={`remove-dest-${idx}`}>
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addDestination}
                      className="text-ocean border-ocean/20 hover:bg-ocean/5 mt-1" data-testid="add-destination-btn">
                      <Plus className="w-3 h-3 mr-1" /> Add Destination
                    </Button>
                  </div>

                  {/* Travelers */}
                  <div>
                    <Label className="text-ocean font-medium">Number of Travelers</Label>
                    <Input type="number" min={1} max={20} value={travelers} onChange={(e) => setTravelers(e.target.value)}
                      className="mt-1 h-10 border-ocean/20 w-24" data-testid="travelers-input" />
                  </div>

                  {/* Transport Mode */}
                  <div>
                    <Label className="text-ocean font-medium mb-2 block">
                      <Car className="w-4 h-4 inline mr-1" /> Transport Mode
                    </Label>
                    <OptionGrid options={TRANSPORT_OPTIONS} selected={transportMode} onSelect={setTransportMode} testIdPrefix="transport" />
                  </div>

                  {/* Accommodation */}
                  <div>
                    <Label className="text-ocean font-medium mb-2 block">
                      <Hotel className="w-4 h-4 inline mr-1" /> Accommodation
                    </Label>
                    <OptionGrid options={ACCOMMODATION_OPTIONS} selected={accommodationType} onSelect={setAccommodationType} testIdPrefix="accommodation" />
                  </div>

                  {/* Activities */}
                  <div>
                    <Label className="text-ocean font-medium mb-2 block">
                      <Camera className="w-4 h-4 inline mr-1" /> Sightseeing & Activities
                    </Label>
                    <OptionGrid options={ACTIVITY_OPTIONS} selected={activities} onSelect={setActivities} testIdPrefix="activity" />
                  </div>

                  <Button onClick={handleCalculate} disabled={loading}
                    className="w-full h-12 bg-gold hover:bg-gold-light text-ocean font-semibold rounded-full shadow-lg text-base"
                    data-testid="calculate-btn">
                    {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Calculator className="w-5 h-5 mr-2" />}
                    {loading ? 'Calculating...' : 'Calculate Budget'}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Results */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              {result ? (
                <BudgetResult result={result} convertedResult={convertedResult}
                  currencyMode={currencyMode} convertingCurrency={convertingCurrency}
                  onToggleCurrency={toggleCurrency} />
              ) : (
                <BudgetPlaceholder />
              )}
            </motion.div>
          </div>

          <div className="flex justify-center mt-8">
            <button onClick={() => navigate('/dashboard')} className="text-white/70 hover:text-white border border-white/30 hover:bg-white/10 rounded-full px-6 py-2 text-sm font-medium flex items-center gap-2 transition-all" data-testid="back-to-dashboard-bottom">
              <ArrowLeft className="w-4 h-4" /> Back to Dashboard
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CalculateBudgetPage;
